import type {
  AdDetails as AdDetailsI,
  SectionView,
  IAdAttributeView,
} from '../../api/types/adviewApiTypes';
import type { NextSeoProps, ProductJsonLdProps } from 'next-seo';
import {
  getJsonSafeString,
  formatMotorDescription,
} from '../../helpers/seo/seoUtils';

export interface AdDetailsSeo {
  title: string;
  canonical?: string;
  description: string;
  openGraph: NextSeoProps['openGraph'];
  twitter: NextSeoProps['twitter'];
  breadcrumbs: SectionView[];
}

export function generateTitle(ad: AdDetailsI): string {
  const type = ad.wanted ? 'wanted' : 'for sale';
  const currency = ad.currency === 'EUR' ? '€' : '£';

  return `${ad.header} ${type} in ${ad.county} for ${currency}${ad.price} on DoneDeal`;
}

export function generateMotorDescription(ad: AdDetailsI) {
  const motorDescription = formatMotorDescription(
    ad.displayAttributes,
    ad.price,
    ad.currency,
    ad.county,
    ad.seller?.name,
    ad.seller?.type === 'DEALER',
  );
  return motorDescription ? getJsonSafeString(motorDescription) : undefined;
}

export function generateDescription(description: string): string {
  const shortDescription =
    description.length > 152 ? `${description.substr(0, 152)}...` : description;
  return getJsonSafeString(shortDescription);
}

export function generateMakeString(
  displayAttributes: AdDetailsI['displayAttributes'],
): string {
  const make =
    displayAttributes &&
    displayAttributes.filter((attribute) => attribute.name === 'make');
  return make?.length ? make[0].value ?? '' : '';
}

export function generateModelString(
  displayAttributes: AdDetailsI['displayAttributes'],
): string {
  const model =
    displayAttributes &&
    displayAttributes.filter((attribute) => attribute.name === 'model');
  return model?.length ? model[0].value ?? '' : '';
}

export function generateOpenGraph(ad: AdDetailsI): NextSeoProps['openGraph'] {
  const make = generateMakeString(ad.displayAttributes);
  const model = generateModelString(ad.displayAttributes);
  const title =
    make.length & model.length
      ? `${generateMakeString(ad.displayAttributes)} ${generateModelString(
          ad.displayAttributes,
        )}`
      : generateTitle(ad);

  const images = [];
  const { photos } = ad;

  if (photos?.length) {
    images.push({
      url: photos[0].small2,
      width: 600,
      height: 450,
    });
  }

  if (photos?.length && photos.length >= 2) {
    images.push({
      url: photos[1].small2,
      width: 600,
      height: 450,
    });
  }

  if (photos?.length && photos.length >= 3) {
    images.push({
      url: photos[2].small2,
      width: 600,
      height: 450,
    });
  }

  return {
    url: ad.friendlyUrl || '',
    type: 'product',
    description: generateDescription(ad.description),
    title,
    ...(images.length ? { images } : {}),
  };
}

export function getAdDetailsSEO(ad: AdDetailsI): AdDetailsSeo {
  return {
    title: generateTitle(ad),
    canonical: ad.friendlyUrl,
    twitter: {
      cardType: 'summary_large_image',
    },
    description:
      ad.section.name === 'cars'
        ? generateMotorDescription(ad) ?? generateDescription(ad.description)
        : generateDescription(ad.description),
    openGraph: generateOpenGraph(ad),
    breadcrumbs: generateBreadcrumbs(ad.breadcrumbs, ad.displayAttributes),
  };
}

export function getCarAdDetailsExtraHeadTags(ad: AdDetailsI): JSX.Element[] {
  if (ad.section.name !== 'cars') return [];
  return [
    <meta
      key="product:category"
      property="product:category"
      content="Vehicles & Parts > Vehicles > Motor Vehicles > Cars, Trucks & Vans"
    />,
    <meta
      key="product:brand"
      property="product:brand"
      content={`${generateMakeString(ad.displayAttributes)}`}
    />,
  ];
}

export function generateAdPrice(adPrice: string | null) {
  if (!adPrice) {
    return '0.00';
  } else {
    return `${adPrice.replace(/,/g, '')}.00`;
  }
}

export function getAdDetailsProductJsonLd(ad: AdDetailsI): ProductJsonLdProps {
  const images = ad?.photos?.map((photo) => photo.small2) || [];
  return {
    productName: generateTitle(ad).replace(/"/g, '&quot;'),
    images: images.slice(0, 3),
    description: generateDescription(ad.description),
    offers: [
      {
        price: generateAdPrice(ad.price),
        priceCurrency: ad.currency,
        itemCondition: 'https://schema.org/UsedCondition',
        availability: 'https://schema.org/InStock',
        url: ad.friendlyUrl,
        seller: {
          name: 'DoneDeal.ie',
        },
      },
    ],
    sku: `${ad.id}`,
    ...(ad.section.name === 'cars'
      ? { brand: generateMakeString(ad.displayAttributes) }
      : {}),
  };
}

export function generateBreadcrumbs(
  breadcrumbs: SectionView[],
  displayAttributes?: IAdAttributeView[],
): SectionView[] {
  const results: SectionView[] = [];
  const sectionsWithMakeFilters = [
    'cars',
    'commercials',
    'motorbikes',
    'tractors',
    'plantmachinery',
    'trucks',
    'mowers',
    'manurespreaders',
  ];
  const sectionsWithMakeModelYearFilters = [
    'cars',
    'commercials',
    'motorbikes',
  ];
  const sectionName = breadcrumbs[breadcrumbs.length - 1].name;

  if (!sectionName) {
    return breadcrumbs;
  }

  if (sectionsWithMakeFilters.includes(sectionName)) {
    const makeAttr = displayAttributes?.find((attr) => attr.name === 'make');
    if (
      makeAttr &&
      makeAttr?.value &&
      makeAttr?.value != 'n/a' &&
      makeAttr?.value != '---'
    ) {
      results.push({
        name: `${sectionName}/${makeAttr.value}`,
        displayName: makeAttr.value,
      });

      if (sectionsWithMakeModelYearFilters.includes(sectionName)) {
        const modelAttr = displayAttributes?.find(
          (attr) => attr.name === 'model',
        );

        if (
          modelAttr &&
          modelAttr.value &&
          modelAttr?.value != 'n/a' &&
          modelAttr?.value != '---'
        ) {
          results.push({
            name: `${sectionName}/${makeAttr?.value}/${modelAttr.value}`,
            displayName: modelAttr.value,
          });

          const yearAttr = displayAttributes?.find(
            (attr) => attr.name === 'year',
          );
          if (
            yearAttr &&
            yearAttr.value &&
            yearAttr?.value != 'n/a' &&
            yearAttr?.value != '---'
          ) {
            results.push({
              name: `${sectionName}/${makeAttr?.value}/${modelAttr.value}/${yearAttr.value}`,
              displayName: yearAttr.value,
            });
          }
        }
      }
    }
  }

  return [...breadcrumbs, ...results];
}
